import { useEffect, useState } from 'react';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { DetailViewCard } from '../../Compounds/CardWithTitle/DetailViewCard';
import { LabelValueField } from '../../Compounds/DetailPanel/LabelValueField';
import { Ticket, updateTicket, handleInfoValidation, deleteTicketById } from '../../../store/Ticket';
import { getFormattedDate } from '../../../utils/helpers';
import { ticketSections } from './ticketHelpers';
import { fetchUsersByRole, isData, isObserver, UserRole } from '../../../store/User';
import { DetailViewValidationCard, ValidatorResult } from '../../Compounds/CardWithTitle';
import { handleRunValidator } from '../../Compounds/CardWithTitle/validationCardHelpers';
import { DetailsComments } from '../../Compounds/DetailsComments';
import { References } from './References';
import { TFUMiniTable } from '../../Compounds/TFU/TFUMiniTable';
import { fetchTicketTFUsDrawer, getTFUDrawerSubject, getTFUDrawerLoadingSubject, removeTFU } from '../../../store/TFU';
import { ETUMiniTable } from '../../Compounds/ETU/ETUMiniTable';
import { fetchETUsDrawer, getETUDrawerSubject, getETUDrawerLoadingSubject, removeETU } from '../../../store/ETU';
import { ConfirmationModal } from '../../Atoms';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { clearDrawerState } from '../../../store/Drawer';

export interface TicketDetailsParams {
  data: Ticket;
}

export const TicketDetails = ({ data }: TicketDetailsParams) => {
  const [validationStatusBySection, setValidationStatusBySection] = useState<Record<string, ValidatorResult>>({});
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const { setSnack } = useSnackbar();

  useEffect(() => {
    fetchUsersByRole([UserRole.ADMIN, UserRole.ANALYST]);
  }, []);

  const handleRemove = async () => {
    const result = await deleteTicketById(data.id, true);
    if (result) {
      setSnack({ message: 'Ticket has been deleted', type: 'success', open: true });
      clearDrawerState();
    }
  };

  return (
    <div
      className="space-y-4"
      data-testid="ticket-details-drawer">
      <DetailViewValidationCard
        data={ticketSections(data, true)}
        detailValidator={(t) =>
          handleRunValidator('info', t, handleInfoValidation, validationStatusBySection, setValidationStatusBySection)
        }
        enableCollapse
        key="ticket-validation-card"
        saveAction={(t) => updateTicket(t as Ticket, true)}
        title="Info"
      />
      <TFUMiniTable
        id={data.id}
        fetchFunction={() => fetchTicketTFUsDrawer(data.id)}
        dataSubject={getTFUDrawerSubject}
        loadingSubject={getTFUDrawerLoadingSubject}
        removeFunction={removeTFU}
        ticketStatus={data?.status}
        drawer
      />
      <ETUMiniTable
        id={data.id}
        fetchFunction={() => fetchETUsDrawer({ ticket: data.id })}
        dataSubject={getETUDrawerSubject}
        loadingSubject={getETUDrawerLoadingSubject}
        removeFunction={removeETU}
        drawer
      />
      <References
        ticket={data}
        drawer
      />
      <DetailsComments
        id={data?.id}
        entityType="ticket"
      />
      <DetailViewCard
        enableCollapse
        key="meta-detail-card"
        title="Meta">
        <div
          className="flex px-4 pt-4 w-full"
          data-testid="meta-detail-card">
          <LabelValueField
            label="Created At"
            value={getFormattedDate(data.createdAt, 'lll')}
            field="createdAt"
          />
          <LabelValueField
            label="Modified At"
            value={getFormattedDate(data.updatedAt, 'lll')}
            field="updatedAt"
          />
        </div>
        <div className="flex px-4 w-full">
          <LabelValueField
            label="Status Date"
            value={getFormattedDate(data.statusDate, 'lll')}
            field="statusDate"
          />
        </div>
        {isObserver() || isData() ? (
          <></>
        ) : (
          <div className="px-4 py-2">
            <div className="w-full border-t border-grey-2 my-3" />
            <button
              className="text-sm text-red rounded my-2 py-2 px-4 bg-[#FFEFEF] hover:bg-[#FDD3D3]"
              onClick={() => setOpenConfirmation(true)}>
              <DeleteIcon
                color="red"
                className="mr-2 -mt-1"
                fontSize="small"
              />
              <span>Delete Ticket</span>
            </button>
            <ConfirmationModal
              open={openConfirmation}
              setOpen={setOpenConfirmation}
              handleSubmit={handleRemove}
              text="Are you sure you want to permanently delete this Ticket?"
              confirmButtonText="Delete"
            />
          </div>
        )}
      </DetailViewCard>
    </div>
  );
};
